import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Building2,
  Plug,
  Users,
  Bell,
  Shield,
  CheckCircle2,
  ExternalLink,
  Lock,
  FileText,
  AlertTriangle,
} from 'lucide-react';

const INTEGRATIONS = [
  { name: 'Epic', status: 'connected', icon: '🏥' },
  { name: 'Cerner', status: 'available', icon: '💊' },
  { name: 'Athenahealth', status: 'available', icon: '📋' },
  { name: 'eClinicalWorks', status: 'available', icon: '🩺' },
];

const AUDIT_LOGS = [
  { timestamp: '2026-01-18 14:32:15', user: 'System', action: 'Claim CLM-XYZ123 auto-submitted', type: 'claim' },
  { timestamp: '2026-01-18 14:28:42', user: 'sarah.jones@beacon.com', action: 'Viewed patient billing details', type: 'access' },
  { timestamp: '2026-01-18 14:15:33', user: 'System', action: 'Denial prevention applied to 12 claims', type: 'ai' },
  { timestamp: '2026-01-18 13:55:21', user: 'admin@beacon.com', action: 'Updated payer configuration', type: 'config' },
];

const SOC2_CONTROLS = [
  { name: 'Access Control', status: 'compliant', description: 'Role-based access enforced' },
  { name: 'Data Encryption', status: 'compliant', description: 'AES-256 at rest, TLS 1.3 in transit' },
  { name: 'Audit Logging', status: 'compliant', description: 'All access logged and retained' },
  { name: 'Incident Response', status: 'compliant', description: 'Automated alerting configured' },
  { name: 'Vendor Management', status: 'review', description: 'Annual review due Feb 2026' },
];

export default function Settings() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Organization configuration and security
        </p>
      </div>

      <Tabs defaultValue="organization" className="space-y-6">
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="organization" className="gap-2">
            <Building2 className="w-4 h-4" />
            Organization
          </TabsTrigger>
          <TabsTrigger value="integrations" className="gap-2">
            <Plug className="w-4 h-4" />
            Integrations
          </TabsTrigger>
          <TabsTrigger value="roles" className="gap-2">
            <Users className="w-4 h-4" />
            Roles
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2">
            <Bell className="w-4 h-4" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2">
            <Shield className="w-4 h-4" />
            Security
          </TabsTrigger>
        </TabsList>

        {/* Organization */}
        <TabsContent value="organization" className="space-y-6">
          <div className="glass-card-elevated p-6 max-w-2xl">
            <h3 className="font-semibold mb-4">Organization Profile</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground">Organization Name</label>
                  <p className="font-medium">Beacon Orthopaedics</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">NPI</label>
                  <p className="font-mono">1234567890</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Tax ID</label>
                  <p className="font-mono">XX-XXXXXXX</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Specialty</label>
                  <p>Orthopedic Surgery</p>
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Address</label>
                <p>500 Medical Center Drive, Suite 200, Cincinnati, OH 45242</p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Integrations */}
        <TabsContent value="integrations" className="space-y-6">
          <div className="grid grid-cols-2 gap-4 max-w-3xl">
            {INTEGRATIONS.map((integration) => (
              <div key={integration.name} className="glass-card p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{integration.icon}</span>
                    <div>
                      <p className="font-medium">{integration.name}</p>
                      <p className="text-xs text-muted-foreground">EHR Integration</p>
                    </div>
                  </div>
                  {integration.status === 'connected' ? (
                    <Badge className="status-badge-success">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Connected
                    </Badge>
                  ) : (
                    <Button variant="outline" size="sm">Connect</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Roles */}
        <TabsContent value="roles" className="space-y-6">
          <div className="glass-card-elevated p-6 max-w-3xl">
            <h3 className="font-semibold mb-4">Roles & Permissions</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-xs font-medium">A</span>
                  </div>
                  <div>
                    <p className="font-medium text-sm">Administrator</p>
                    <p className="text-xs text-muted-foreground">Full system access</p>
                  </div>
                </div>
                <Badge variant="outline">2 users</Badge>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <span className="text-xs font-medium">B</span>
                  </div>
                  <div>
                    <p className="font-medium text-sm">Billing Manager</p>
                    <p className="text-xs text-muted-foreground">Claims, payments, reports</p>
                  </div>
                </div>
                <Badge variant="outline">4 users</Badge>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-xs font-medium">V</span>
                  </div>
                  <div>
                    <p className="font-medium text-sm">Viewer</p>
                    <p className="text-xs text-muted-foreground">Read-only dashboard access</p>
                  </div>
                </div>
                <Badge variant="outline">8 users</Badge>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications" className="space-y-6">
          <div className="glass-card-elevated p-6 max-w-2xl">
            <h3 className="font-semibold mb-4">Notification Preferences</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                <div>
                  <p className="font-medium text-sm">Denial Alerts</p>
                  <p className="text-xs text-muted-foreground">Notify when claims are denied</p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                <div>
                  <p className="font-medium text-sm">Payment Received</p>
                  <p className="text-xs text-muted-foreground">Notify on ERA/EOB posting</p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                <div>
                  <p className="font-medium text-sm">Payer Policy Changes</p>
                  <p className="text-xs text-muted-foreground">Alert on detected policy drift</p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                <div>
                  <p className="font-medium text-sm">Daily Summary</p>
                  <p className="text-xs text-muted-foreground">Daily digest of key metrics</p>
                </div>
                <Switch />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Security */}
        <TabsContent value="security" className="space-y-6">
          {/* HIPAA Mode */}
          <div className="glass-card-elevated p-6 max-w-3xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-primary" />
                <div>
                  <h3 className="font-semibold">HIPAA Compliance Mode</h3>
                  <p className="text-xs text-muted-foreground">Enhanced security controls enabled</p>
                </div>
              </div>
              <Badge className="status-badge-success">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Active
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="p-3 rounded-lg bg-success/5 border border-success/20">
                <CheckCircle2 className="w-4 h-4 text-success mb-2" />
                <p className="font-medium">PHI De-identified</p>
              </div>
              <div className="p-3 rounded-lg bg-success/5 border border-success/20">
                <CheckCircle2 className="w-4 h-4 text-success mb-2" />
                <p className="font-medium">Encryption Active</p>
              </div>
              <div className="p-3 rounded-lg bg-success/5 border border-success/20">
                <CheckCircle2 className="w-4 h-4 text-success mb-2" />
                <p className="font-medium">Access Logged</p>
              </div>
            </div>
          </div>

          {/* SOC2 Controls */}
          <div className="glass-card-elevated p-6 max-w-3xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-primary" />
                <h3 className="font-semibold">SOC 2 Controls</h3>
              </div>
              <Button variant="outline" size="sm" className="gap-2">
                <FileText className="w-4 h-4" />
                Download Report
              </Button>
            </div>
            <div className="space-y-2">
              {SOC2_CONTROLS.map((control) => (
                <div key={control.name} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                  <div>
                    <p className="font-medium text-sm">{control.name}</p>
                    <p className="text-xs text-muted-foreground">{control.description}</p>
                  </div>
                  <Badge className={control.status === 'compliant' ? "status-badge-success" : "status-badge-warning"}>
                    {control.status === 'compliant' ? (
                      <><CheckCircle2 className="w-3 h-3 mr-1" />Compliant</>
                    ) : (
                      <><AlertTriangle className="w-3 h-3 mr-1" />Review</>
                    )}
                  </Badge>
                </div>
              ))}
            </div>
          </div>

          {/* Audit Logs */}
          <div className="glass-card-elevated p-6 max-w-4xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-primary" />
                <h3 className="font-semibold">Audit Logs</h3>
              </div>
              <Button variant="outline" size="sm">View All</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {AUDIT_LOGS.map((log, i) => (
                    <tr key={i}>
                      <td className="font-mono text-xs">{log.timestamp}</td>
                      <td className="text-sm">{log.user}</td>
                      <td className="text-sm">{log.action}</td>
                      <td>
                        <Badge variant="outline" className="text-[10px]">{log.type}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
